import { prepareSubmission, completeSubmission } from "../utils/researchMeasurement";
import { create } from "zustand";
import { fetchProgressBatch, replaceRequestedProgress } from "../utils/progressBatch";
import { createRequestDeduplicator } from "../utils/inFlightRequests";
import { devtools, persist } from "zustand/middleware";
import useCoursesStore, { type CodingExerciseType } from "./coursesStore";
import { authenticatedFetch } from "../utils/authFetch";
import {
  readCodingScoreBreakdown,
  type CodingScoreBreakdown,
} from "../utils/codingGrading";
import {
  mergePersistedProgressState,
  selectPersistedProgressState,
} from "../utils/progressPersistence";
import { requireLessonRevisionContext } from "../utils/contentRevisions";

interface Progress {
  lessonProgress: {
    [key: string]: {
      video_completed: boolean;
      quiz_completed: boolean;
      coding_completed: boolean;
      emotion_completed: boolean;
      has_quiz: boolean;
      has_coding: boolean;
      lesson_completed: boolean;
      last_updated: string;
    };
  };
  videoProgress: {
    [key: string]: {
      video_position_seconds: number;
      total_watch_time_seconds: number;
      completed: boolean;
      last_updated: string;
    };
  };
  quizProgress: {
    [key: string]: {
      questions_answered: number;
      questions_graded: number;
      total_questions: number;
      progress_percent: number;
      score_percent: number | null;
      completed: boolean;
    };
  };
  unitProgress: {
    [key: string]: {
      lessons_completed: number;
      total_lessons: number;
      progress_percent: number;
      completed: boolean;
    };
  };
  courseProgress: {
    [key: string]: {
      units_completed: number;
      total_units: number;
      progress_percent: number;
      completed: boolean;
    };
  };
  quizAnswers: {
    [key: string]: {
      is_correct: boolean;
      selected_option_id?: number;
      short_answer?: string;
      llm_score?: number;
      llm_feedback?: string;
    };
  };
  quizSessionState: {
    [key: string]: QuizSessionState;
  };
  codingSubmissions: {
    [key: string]: CodingSubmission;
  };
  codingDrafts: {
    [key: string]: CodingDraft;
  };
  exerciseMetadata: {
    [key: string]: ExerciseSubmissionMetadata; // Key: ${studentId}-${exerciseId}
  };
  // Track which lessons have had coding submissions fetched (key: ${studentId}-${lessonId})
  fetchedCodingSubmissionsForLessons: Set<string>;
}

export interface CodingSubmission {
  exposure_id?: string;
  attempt_number?: number;
  active_answer_ms?: number;
  elapsed_answer_ms?: number;
  submission_id?: string;
  attempt_id?: number;
  llm_score: number;
  score_breakdown?: CodingScoreBreakdown;
  llm_feedback: string;
  llm_suggestions: string | string[] | null;
  submitted_at: string;
  submitted_code: string; // Store the actual code that was submitted
  generated_exercise?: CodingExerciseType; // Store generated exercise for retry
}

export interface CodingDraft {
  code: string;
  savedAt: string;
}

export interface ExerciseSubmissionMetadata {
  exerciseId: number;
  originalExerciseId?: number; // Link to the original exercise this retries
  isRetry: boolean; // True if this is a generated retry exercise
}

export interface QuizSubmissionResult {
  exposure_id?: string;
  submission_id?: string;
  attempt_id?: number;
  attempt_number?: number;
  active_answer_ms?: number;
  elapsed_answer_ms?: number;
  is_correct: boolean;
  selected_option_id?: number;
  short_answer?: string;
  llm_score?: number;
  llm_feedback?: string;
}

export interface QuizSessionState {
  currentQuestionIndex: number;
  attemptIndexByQuestionIndex: Record<number, number>;
  updated_at: string;
}

interface ProgressStore extends Progress {
  // Actions for lesson progress
  optimisticallyMarkVideoCompleted: (
    studentId: string,
    lessonId: number,
    videoPositionSeconds?: number,
    watchDurationSeconds?: number
  ) => void;
  markVideoCompleted: (
    studentId: string,
    lessonId: number,
    videoPositionSeconds?: number,
    watchDurationSeconds?: number
  ) => Promise<void>;
  updateVideoProgress: (
    studentId: string,
    lessonId: number,
    videoPositionSeconds?: number,
    watchDurationSeconds?: number,
    completed?: boolean
  ) => Promise<void>;
  updateCodingProgress: (
    studentId: string,
    lessonId: number,
    completed?: boolean
  ) => Promise<void>;
  markCodingCompleted: (studentId: string, lessonId: number) => Promise<void>;
  submitQuizAnswer: (
    studentId: string,
    quizId: number,
    questionId: number,
    answer: {
      selected_option_id?: number;
      short_answer?: string;
    }
  ) => Promise<QuizSubmissionResult>;
  submitCodingExercise: (
    studentId: string,
    exerciseId: number,
    code: string
  ) => Promise<void>;
  generateCodingExercise: (
    studentId: string,
    exerciseId: number
  ) => Promise<CodingExerciseType | null>;
  saveCodingDraft: (
    studentId: string,
    exerciseId: number,
    code: string
  ) => void;
  getCodingDraft: (
    studentId: string,
    exerciseId: number
  ) => CodingDraft | undefined;
  clearCodingDraft: (studentId: string, exerciseId: number) => void;
  setExerciseMetadata: (
    studentId: string,
    exerciseId: number,
    metadata: ExerciseSubmissionMetadata
  ) => void;
  getExerciseMetadata: (
    studentId: string,
    exerciseId: number
  ) => ExerciseSubmissionMetadata | undefined;
  submitLessonEmotion: (
    studentId: string,
    lessonId: number,
    emotion: "engaged" | "confused" | "frustrated" | "bored"
  ) => Promise<void>;
  startSession: (studentId: string, lessonId?: number) => Promise<number>;
  endSession: (
    sessionId: number,
    options?: { keepalive?: boolean }
  ) => Promise<void>;

  // Fetch progress data
  fetchLessonProgressBatch: (studentId: string, lessonIds: number[]) => Promise<void>;
  fetchUnitProgressBatch: (studentId: string, unitIds: number[]) => Promise<void>;
  fetchLessonProgress: (studentId: string, lessonId: number) => Promise<void>;
  fetchVideoProgress: (studentId: string, lessonId: number) => Promise<void>;
  fetchQuizProgress: (studentId: string, quizId: number) => Promise<void>;
  setQuizSessionState: (
    studentId: string,
    lessonId: number,
    sessionState: {
      currentQuestionIndex: number;
      attemptIndexByQuestionIndex: Record<number, number>;
    }
  ) => void;
  getQuizSessionState: (
    studentId: string,
    lessonId: number
  ) => QuizSessionState | undefined;
  clearQuizSessionState: (studentId: string, lessonId: number) => void;
  fetchCodingSubmissions: (studentId: string, lessonId: number) => Promise<void>;
  fetchUnitProgress: (studentId: string, unitId: number) => Promise<void>;
  fetchCourseProgress: (studentId: string, courseId: number) => Promise<void>;

  // Reset store
  reset: () => void;
  purgeOtherUsers: (studentId: string) => void;
}

const initialState: Progress = {
  lessonProgress: {},
  videoProgress: {},
  quizProgress: {},
  quizSessionState: {},
  unitProgress: {},
  courseProgress: {},
  quizAnswers: {},
  codingSubmissions: {},
  codingDrafts: {},
  exerciseMetadata: {},
  fetchedCodingSubmissionsForLessons: new Set(),
};

// Add API base URL constant
const API_BASE_URL = import.meta.env.VITE_BACKEND_API_URL; // or your actual API base URL

const loadedLessonRevisionContext = (lessonId?: number | null) =>
  requireLessonRevisionContext(
    lessonId
      ? useCoursesStore.getState().lessonsContent[lessonId]?.data
      : undefined
  );

const deduplicateProgressRequest = createRequestDeduplicator();
let progressGeneration = 0;
let progressStudentId: string | null = null;

export const useProgressStore = create<ProgressStore>()(
  devtools(
    persist(
      (set, get) => ({
        ...initialState,

        // @deprecated - Use useAnalytics().startAppSession() instead
        // This method calls a removed /start-session endpoint (404)
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        startSession: async (_studentId: string, _lessonId?: number) => {
          console.warn(
            "progressStore.startSession is deprecated. Use useAnalytics().startAppSession() instead."
          );
          return -1;
        },

        // @deprecated - Use useAnalytics().endAppSession() instead
        // This method calls a removed /end-session endpoint (404)
        endSession: async (
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          _sessionId: number,
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          _options?: { keepalive?: boolean }
        ) => {
          console.warn(
            "progressStore.endSession is deprecated. Use useAnalytics().endAppSession() instead."
          );
        },

        optimisticallyMarkVideoCompleted: (
          studentId: string,
          lessonId: number,
          videoPositionSeconds = 0,
          watchDurationSeconds = 0
        ) => {
          if (!studentId || !lessonId) {
            return;
          }
          set((state) => {
            const lessonKey = `${studentId}-${lessonId}`;
            const currentLesson = state.lessonProgress[lessonKey];
            const videoDone = true;
            const quizDone = !currentLesson?.has_quiz || currentLesson?.quiz_completed;
            const codingDone = !currentLesson?.has_coding || currentLesson?.coding_completed;

            return {
              videoProgress: {
                ...state.videoProgress,
                [lessonKey]: {
                  video_position_seconds: Math.round(videoPositionSeconds),
                  total_watch_time_seconds: Math.round(watchDurationSeconds),
                  completed: true,
                  last_updated: new Date().toISOString(),
                },
              },
              lessonProgress: currentLesson
                ? {
                    ...state.lessonProgress,
                    [lessonKey]: {
                      ...currentLesson,
                      video_completed: true,
                      lesson_completed:
                        videoDone &&
                        (quizDone ?? false) &&
                        (codingDone ?? false) &&
                        currentLesson.emotion_completed,
                    },
                  }
                : state.lessonProgress,
            };
          });
        },
        updateVideoProgress: async (
          studentId: string,
          lessonId: number,
          videoPositionSeconds = 0,
          watchDurationSeconds = 0,
          completed = false
        ) => {
          try {
            if (!studentId || !lessonId) {
              throw new Error("Student ID and Lesson ID are required");
            }

            const response = await authenticatedFetch(`${API_BASE_URL}/video-progress`, {
              method: "POST",
              credentials: "include",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                student_id: studentId,
                lesson_id: lessonId,
                video_position_seconds: Math.round(videoPositionSeconds),
                watch_duration_seconds: Math.round(watchDurationSeconds),
                completed,
                video_revision: loadedLessonRevisionContext(lessonId).video_revision,
              }),
            }, { studentId, lessonId });

            if (!response.ok) {
              const errorData = await response.json().catch(() => null);
              throw new Error(
                errorData?.detail || "Failed to update video progress"
              );
            }

            const data = await response.json();
            set((state) => {
              const lessonKey = `${studentId}-${lessonId}`;
              const currentLesson = state.lessonProgress[lessonKey];
              const videoDone = completed || currentLesson?.video_completed;
              const quizDone = !currentLesson?.has_quiz || currentLesson?.quiz_completed;
              const codingDone = !currentLesson?.has_coding || currentLesson?.coding_completed;

              return {
                videoProgress: {
                  ...state.videoProgress,
                  [lessonKey]: {
                    video_position_seconds: Math.round(videoPositionSeconds),
                    total_watch_time_seconds: Math.round(watchDurationSeconds),
                    completed,
                    last_updated: new Date().toISOString(),
                  },
                },
                lessonProgress: currentLesson
                  ? {
                      ...state.lessonProgress,
                      [lessonKey]: {
                        ...currentLesson,
                        video_completed: videoDone ?? false,
                        lesson_completed:
                          (videoDone ?? false) &&
                          (quizDone ?? false) &&
                          (codingDone ?? false) &&
                          currentLesson.emotion_completed,
                      },
                    }
                  : state.lessonProgress,
              };
            });

            return data;
          } catch (error) {
            console.error("Error updating video progress:", error);
            throw error;
          }
        },
        markVideoCompleted: async (
          studentId: string,
          lessonId: number,
          videoPositionSeconds = 0,
          watchDurationSeconds = 0
        ) => {
          get().optimisticallyMarkVideoCompleted(
            studentId,
            lessonId,
            videoPositionSeconds,
            watchDurationSeconds
          );
          await get().updateVideoProgress(
            studentId,
            lessonId,
            videoPositionSeconds,
            watchDurationSeconds,
            true
          );

          // After marking video complete, fetch updated lesson progress
          await get().fetchLessonProgress(studentId, lessonId);
        },
        updateCodingProgress: async (
          studentId: string,
          lessonId: number
        ) => {
          try {
            if (!studentId || !lessonId) {
              throw new Error("Student ID and Lesson ID are required");
            }

            const response = await authenticatedFetch(
              `${API_BASE_URL}/coding-progress`,
              {
                method: "POST",
                credentials: "include",
                headers: {
                  "Content-Type": "application/json",
                },
                body: JSON.stringify({
                  student_id: studentId,
                  lesson_id: lessonId,
                }),
              },
              { studentId, lessonId }
            );

            if (!response.ok) {
              const errorData = await response.json().catch(() => null);
              throw new Error(
                errorData?.detail || "Failed to update coding progress"
              );
            }
            await response.json().catch(() => null);
            await get().fetchLessonProgress(studentId, lessonId);
          } catch (error) {
            console.error("Error updating coding progress:", error);
            throw error;
          }
        },
        markCodingCompleted: async (studentId: string, lessonId: number) => {
          await get().fetchLessonProgress(studentId, lessonId);
        },
        fetchVideoProgress: async (studentId: string, lessonId: number) => {
          try {
            const response = await authenticatedFetch(
              `${API_BASE_URL}/video-progress/${studentId}/${lessonId}`,
              {
                credentials: "include",
              },
              { studentId, lessonId }
            );

            if (!response.ok) {
              const errorData = await response.json().catch(() => null);
              throw new Error(
                errorData?.detail || "Failed to fetch video progress"
              );
            }

            const data = await response.json();
            const progress = Array.isArray(data) ? data[0] : null;
            if (progress) {
              set((state) => ({
                videoProgress: {
                  ...state.videoProgress,
                  [`${studentId}-${lessonId}`]: progress,
                },
              }));
            }
          } catch (error) {
            console.error("Error fetching video progress:", error);
            throw error;
          }
        },

        submitQuizAnswer: async (
          studentId: string,
          quizId: number,
          questionId: number,
          answer: { selected_option_id?: number; short_answer?: string }
        ) => {
          try {
            const revisions = loadedLessonRevisionContext(useCoursesStore.getState().currentLessonId);
            const measurement = await prepareSubmission(studentId, "quiz", questionId, { ...answer, ...revisions });
            let response;
            if (answer.selected_option_id !== undefined) {
              response = await authenticatedFetch(`${API_BASE_URL}/mcq-answer`, {
                method: "POST",
                credentials: "include",
                headers: {
                  "Content-Type": "application/json",
                },
                body: JSON.stringify({
                  student_id: studentId,
                  question_id: questionId,
                  selected_option_id: answer.selected_option_id,
                  ...revisions,
                  ...measurement,
                }),
              }, { studentId });
            } else if (answer.short_answer) {
              response = await authenticatedFetch(`${API_BASE_URL}/short-answer-question`, {
                method: "POST",
                credentials: "include",
                headers: {
                  "Content-Type": "application/json",
                },
                body: JSON.stringify({
                  student_id: studentId,
                  question_id: questionId,
                  short_answer: answer.short_answer,
                  ...revisions,
                  ...measurement,
                }),
              }, { studentId });
            }

            if (!response?.ok) {
              const errorData = await response?.json();
              throw new Error(
                errorData?.detail || "Failed to submit quiz answer"
              );
            }

            const result = await response.json();

            // Store the answer result
            set((state) => ({
              quizAnswers: {
                ...state.quizAnswers,
                [`${studentId}-${questionId}`]: {
                  is_correct: result.is_correct,
                  selected_option_id: answer.selected_option_id,
                  short_answer: answer.short_answer,
                  llm_score: result.llm_score,
                  llm_feedback: result.llm_feedback,
                },
              },
            }));

            // Update the quiz progress in the store
            const promises: Promise<void>[] = [
              get().fetchQuizProgress(studentId, quizId)
            ];

            // Update lesson progress if we have a current lesson
            const lessonId = useCoursesStore.getState().currentLessonId;
            if (lessonId) {
              promises.push(get().fetchLessonProgress(studentId, lessonId));
            }

            completeSubmission(studentId, "quiz", questionId);
            // Show the graded answer right away; progress (and the coding
            // unlock) refreshes in the background and re-renders when it lands.
            void Promise.allSettled(promises);

            return result;
          } catch (error) {
            console.error("Error submitting quiz answer:", error);
            throw error;
          }
        },

        submitCodingExercise: async (
          studentId: string,
          exerciseId: number,
          code: string
        ) => {
          try {
            const revisions = loadedLessonRevisionContext(useCoursesStore.getState().currentLessonId);
            const measurement = await prepareSubmission(studentId, "coding", exerciseId, { code, ...revisions });
            const response = await authenticatedFetch(`${API_BASE_URL}/coding-answer`, {
              method: "POST",
              credentials: "include",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                student_id: studentId,
                coding_exercise_id: exerciseId,
                code,
                ...revisions,
                ...measurement,
              }),
            }, { studentId });

            if (!response.ok) {
              const errorData = await response.json().catch(() => null);
              throw new Error(
                errorData?.detail || "Failed to submit coding exercise"
              );
            }

            const result = await response.json();

            if (typeof result.evaluation?.score !== "number" ||
                !Number.isFinite(result.evaluation.score) || result.evaluation.score < 0 || result.evaluation.score > 100) {
              throw new Error("Could not confirm the grade. Please try again.");
            }

            // Extract generated exercise if available
            const generatedExercise = result.evaluation?.generated_exercise;

            // Store the submission result with submitted code and generated exercise
            set((state) => ({
              codingSubmissions: {
                ...state.codingSubmissions,
                [`${studentId}-${exerciseId}`]: {
                  exposure_id: result.exposure_id,
                  attempt_number: result.attempt_number,
                  active_answer_ms: result.active_answer_ms,
                  elapsed_answer_ms: result.elapsed_answer_ms,
                  submission_id: result.submission_id,
                  attempt_id: result.attempt_id,
                  llm_score: result.evaluation.score,
                  score_breakdown: readCodingScoreBreakdown(result.evaluation),
                  llm_feedback: result.evaluation.feedback,
                  llm_suggestions: result.evaluation.suggestions ?? null,
                  submitted_at: new Date().toISOString(),
                  submitted_code: code, // Store the submitted code
                  generated_exercise: generatedExercise, // Store generated exercise for retry
                },
              },
            }));

            if (generatedExercise) {
              set((state) => ({
                exerciseMetadata: {
                  ...state.exerciseMetadata,
                  [`${studentId}-${generatedExercise.id}`]: {
                    exerciseId: generatedExercise.id,
                    originalExerciseId: exerciseId,
                    isRetry: true,
                  },
                },
              }));
            }

            completeSubmission(studentId, "coding", exerciseId);
            // Clear the draft after successful submission
            get().clearCodingDraft(studentId, exerciseId);

            const lessonId = useCoursesStore.getState().currentLessonId;
            if (lessonId) {
              // Only update lesson progress, NOT lesson content
              // The generated exercise is already stored in codingSubmissions via the API response
              // Calling fetchLessonContent here causes a race condition that resets the exercise chain
              await Promise.allSettled([get().fetchLessonProgress(studentId, lessonId)]);
            }

            return result;
          } catch (error) {
            console.error("Error submitting coding exercise:", error);
            throw error;
          }
        },

        generateCodingExercise: async (studentId: string, exerciseId: number) => {
          try {
            if (!studentId || !exerciseId) {
              throw new Error("Student ID and Exercise ID are required");
            }

            const response = await authenticatedFetch(
              `${API_BASE_URL}/coding-generate`,
              {
                method: "POST",
                credentials: "include",
                headers: {
                  "Content-Type": "application/json",
                },
                body: JSON.stringify({
                  student_id: studentId,
                  coding_exercise_id: exerciseId,
                  ...loadedLessonRevisionContext(
                    useCoursesStore.getState().currentLessonId
                  ),
                }),
              },
              { studentId }
            );

            if (!response.ok) {
              const errorData = await response.json().catch(() => null);
              throw new Error(
                errorData?.detail || "Failed to generate coding exercise"
              );
            }

            const data = await response.json();
            const generatedExercise = data?.generated_exercise ?? null;

            if (generatedExercise) {
              set((state) => {
                const key = `${studentId}-${exerciseId}`;
                const existing = state.codingSubmissions[key];
                if (!existing) {
                  return state;
                }
                return {
                  codingSubmissions: {
                    ...state.codingSubmissions,
                    [key]: {
                      ...existing,
                      generated_exercise: generatedExercise,
                    },
                  },
                };
              });
            }

            return generatedExercise;
          } catch (error) {
            console.error("Error generating coding exercise:", error);
            throw error;
          }
        },

        saveCodingDraft: (
          studentId: string,
          exerciseId: number,
          code: string
        ) => {
          try {
            set((state) => ({
              codingDrafts: {
                ...state.codingDrafts,
                [`${studentId}-${exerciseId}`]: {
                  code,
                  savedAt: new Date().toISOString(),
                },
              },
            }));
          } catch (error) {
            // Handle localStorage quota exceeded or other storage errors
            if (
              error instanceof Error &&
              (error.name === "QuotaExceededError" ||
                error.message.includes("quota"))
            ) {
              console.warn(
                "localStorage quota exceeded. Draft save failed. Consider clearing old drafts."
              );
            } else {
              console.error("Error saving coding draft:", error);
            }
            // Silently fail - keep in-memory state only
          }
        },

        getCodingDraft: (studentId: string, exerciseId: number) => {
          const key = `${studentId}-${exerciseId}`;
          return get().codingDrafts[key];
        },

        clearCodingDraft: (studentId: string, exerciseId: number) => {
          set((state) => {
            const newDrafts = { ...state.codingDrafts };
            delete newDrafts[`${studentId}-${exerciseId}`];
            return { codingDrafts: newDrafts };
          });
        },

        setExerciseMetadata: (
          studentId: string,
          exerciseId: number,
          metadata: ExerciseSubmissionMetadata
        ) => {
          set((state) => ({
            exerciseMetadata: {
              ...state.exerciseMetadata,
              [`${studentId}-${exerciseId}`]: metadata,
            },
          }));
        },

        getExerciseMetadata: (studentId: string, exerciseId: number) => {
          const key = `${studentId}-${exerciseId}`;
          return get().exerciseMetadata[key];
        },

        submitLessonEmotion: async (
          studentId: string,
          lessonId: number,
          emotion: "engaged" | "confused" | "frustrated" | "bored"
        ) => {
          try {
            if (!studentId || !lessonId) {
              throw new Error("Student ID and Lesson ID are required");
            }

            const response = await authenticatedFetch(`${API_BASE_URL}/lesson-emotion`, {
              method: "POST",
              credentials: "include",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                student_id: studentId,
                lesson_id: lessonId,
                emotion,
              }),
            }, { studentId, lessonId });

            if (!response.ok) {
              const errorData = await response.json().catch(() => null);
              throw new Error(
                errorData?.detail || "Failed to submit lesson emotion"
              );
            }

            // Update local state immediately for instant UI feedback
            set((state) => {
              const key = `${studentId}-${lessonId}`;
              const currentProgress = state.lessonProgress[key];
              if (!currentProgress) return state;

              const videoDone = currentProgress.video_completed;
              const quizDone = !currentProgress.has_quiz || currentProgress.quiz_completed;
              const codingDone = !currentProgress.has_coding || currentProgress.coding_completed;

              return {
                lessonProgress: {
                  ...state.lessonProgress,
                  [key]: {
                    ...currentProgress,
                    emotion_completed: true,
                    lesson_completed: videoDone && quizDone && codingDone,
                  },
                },
              };
            });
          } catch (error) {
            console.error("Error submitting lesson emotion:", error);
            throw error;
          }
        },

        fetchLessonProgressBatch: (studentId, ids) => {
          const generation = progressGeneration;
          const uniqueIds = [...new Set(ids)].sort((a, b) => a - b);
          return deduplicateProgressRequest(
            `${generation}:lesson:${studentId}:${uniqueIds.join(",")}`,
            async () => {
              const rows = await fetchProgressBatch<
                Progress["lessonProgress"][string] & { lesson_id: number }
              >("lesson", studentId, uniqueIds);
              if (generation !== progressGeneration) return;
              set((state) => ({
                lessonProgress: replaceRequestedProgress(
                  state.lessonProgress, studentId, uniqueIds,
                  rows.map((row) => ({ id: row.lesson_id, progress: row }))
                ),
              }));
            }
          );
        },

        fetchLessonProgress: async (studentId: string, lessonId: number) => {
          try {
            const response = await authenticatedFetch(
              `${API_BASE_URL}/lesson-progress/${studentId}/${lessonId}`,
              {
                credentials: "include",
              },
              { studentId, lessonId }
            );
            if (!response.ok)
              throw new Error("Failed to fetch lesson progress");

            const data = await response.json();
            const progress = Array.isArray(data) ? data[0] : data;
            if (progress) {
              set((state) => ({
                lessonProgress: {
                  ...state.lessonProgress,
                  [`${studentId}-${lessonId}`]: progress,
                },
              }));
            }
          } catch (error) {
            console.error("Error fetching lesson progress:", error);
            throw error;
          }
        },

        fetchQuizProgress: async (studentId: string, quizId: number) => {
          try {
            const response = await authenticatedFetch(
              `${API_BASE_URL}/quiz-progress/${studentId}/${quizId}`,
              {
                credentials: "include",
              },
              { studentId }
            );
            if (!response.ok) throw new Error("Failed to fetch quiz progress");

            const data = await response.json();
            const progress = Array.isArray(data) ? data[0] : data;
            if (progress) {
              set((state) => {
                const lessonKey = `${studentId}-${quizId}`;
                const currentLesson = state.lessonProgress[lessonKey];
                const quizDone = progress.completed ?? false;
                const videoDone = currentLesson?.video_completed ?? false;
                const codingDone = !currentLesson?.has_coding || currentLesson?.coding_completed;

                return {
                  quizProgress: {
                    ...state.quizProgress,
                    [lessonKey]: progress,
                  },
                  lessonProgress: currentLesson
                    ? {
                        ...state.lessonProgress,
                        [lessonKey]: {
                          ...currentLesson,
                          quiz_completed: quizDone,
                          lesson_completed:
                            videoDone &&
                            (quizDone || !currentLesson?.has_quiz) &&
                            (codingDone ?? false) &&
                            currentLesson.emotion_completed,
                        },
                      }
                    : state.lessonProgress,
                };
              });
            }
          } catch (error) {
            console.error("Error fetching quiz progress:", error);
            throw error;
          }
        },

        setQuizSessionState: (studentId, lessonId, sessionState) => {
          if (!studentId || !lessonId) {
            return;
          }

          const normalizedAttemptIndexes = Object.entries(
            sessionState.attemptIndexByQuestionIndex ?? {}
          ).reduce<Record<number, number>>((accumulator, [questionIndex, attemptIndex]) => {
            const parsedQuestionIndex = Number(questionIndex);
            if (!Number.isInteger(parsedQuestionIndex) || parsedQuestionIndex < 0) {
              return accumulator;
            }
            if (!Number.isInteger(attemptIndex) || attemptIndex < 0) {
              return accumulator;
            }
            accumulator[parsedQuestionIndex] = attemptIndex;
            return accumulator;
          }, {});

          set((state) => ({
            quizSessionState: {
              ...state.quizSessionState,
              [`${studentId}-${lessonId}`]: {
                currentQuestionIndex: Math.max(
                  0,
                  Math.floor(sessionState.currentQuestionIndex ?? 0)
                ),
                attemptIndexByQuestionIndex: normalizedAttemptIndexes,
                updated_at: new Date().toISOString(),
              },
            },
          }));
        },

        getQuizSessionState: (studentId, lessonId) =>
          get().quizSessionState[`${studentId}-${lessonId}`],

        clearQuizSessionState: (studentId, lessonId) => {
          if (!studentId || !lessonId) {
            return;
          }
          set((state) => {
            const nextQuizSessionState = { ...state.quizSessionState };
            delete nextQuizSessionState[`${studentId}-${lessonId}`];
            return {
              quizSessionState: nextQuizSessionState,
            };
          });
        },

        fetchCodingSubmissions: async (studentId: string, lessonId: number) => {
          try {
            // Check if already fetched for this lesson
            const cacheKey = `${studentId}-${lessonId}`;
            if (get().fetchedCodingSubmissionsForLessons.has(cacheKey)) {
              return; // Already fetched, skip
            }

            const response = await authenticatedFetch(
              `${API_BASE_URL}/coding-submissions/${studentId}/${lessonId}`,
              {
                credentials: "include",
              },
              { studentId, lessonId }
            );

            if (!response.ok) {
              throw new Error(
                `Failed to fetch coding submissions: ${response.status}`
              );
            }

            const data = await response.json();

            // Populate codingSubmissions state with fetched data
            const newSubmissions: Record<string, CodingSubmission> = {};
            const newMetadata: Record<string, ExerciseSubmissionMetadata> = {};

            for (const sub of data.submissions) {
              const key = `${studentId}-${sub.coding_exercise_id}`;
              newSubmissions[key] = {
                llm_score: sub.llm_score,
                score_breakdown: readCodingScoreBreakdown(sub),
                llm_feedback: sub.llm_feedback,
                llm_suggestions: sub.llm_suggestions ?? null,
                submitted_at: sub.submitted_at,
                submitted_code: sub.code,
                generated_exercise: sub.generated_exercise,
              };

              // Track metadata for retry chains
              if (sub.generated_exercise) {
                const genKey = `${studentId}-${sub.generated_exercise.id}`;
                newMetadata[genKey] = {
                  exerciseId: sub.generated_exercise.id,
                  isRetry: true,
                  originalExerciseId: sub.coding_exercise_id,
                };
              }
            }

            set((state) => {
              // Create new Set with the cache key added
              const updatedSet = new Set(state.fetchedCodingSubmissionsForLessons);
              updatedSet.add(cacheKey);

              return {
                codingSubmissions: { ...state.codingSubmissions, ...newSubmissions },
                exerciseMetadata: { ...state.exerciseMetadata, ...newMetadata },
                fetchedCodingSubmissionsForLessons: updatedSet,
              };
            });
          } catch (error) {
            console.error("Error fetching coding submissions:", error);
          }
        },

        fetchUnitProgressBatch: (studentId, ids) => {
          const generation = progressGeneration;
          const uniqueIds = [...new Set(ids)].sort((a, b) => a - b);
          return deduplicateProgressRequest(
            `${generation}:unit:${studentId}:${uniqueIds.join(",")}`,
            async () => {
              const rows = await fetchProgressBatch<
                Progress["unitProgress"][string] & { unit_id: number }
              >("unit", studentId, uniqueIds);
              if (generation !== progressGeneration) return;
              set((state) => ({
                unitProgress: replaceRequestedProgress(
                  state.unitProgress, studentId, uniqueIds,
                  rows.map((row) => ({ id: row.unit_id, progress: row }))
                ),
              }));
            }
          );
        },

        fetchUnitProgress: async (studentId: string, unitId: number) => {
          try {
            const response = await authenticatedFetch(
              `${API_BASE_URL}/unit-progress/${studentId}/${unitId}`,
              {
                credentials: "include",
              },
              { studentId, unitId }
            );
            if (!response.ok) throw new Error("Failed to fetch unit progress");

            const data = await response.json();
            const progress = Array.isArray(data) ? data[0] : data;
            if (progress) {
              set((state) => ({
                unitProgress: {
                  ...state.unitProgress,
                  [`${studentId}-${unitId}`]: progress,
                },
              }));
            }
          } catch (error) {
            console.error("Error fetching unit progress:", error);
            throw error;
          }
        },

        fetchCourseProgress: async (studentId: string, courseId: number) => {
          try {
            const response = await authenticatedFetch(
              `${API_BASE_URL}/course-progress/${studentId}/${courseId}`,
              {
                credentials: "include",
              },
              { studentId, courseId }
            );
            if (!response.ok)
              throw new Error("Failed to fetch course progress");

            const data = await response.json();
            const progress = Array.isArray(data) ? data[0] : data;
            if (progress) {
              set((state) => ({
                courseProgress: {
                  ...state.courseProgress,
                  [`${studentId}-${courseId}`]: progress,
                },
              }));
            }
          } catch (error) {
            console.error("Error fetching course progress:", error);
            throw error;
          }
        },

        reset: () => {
          progressGeneration += 1;
          progressStudentId = null;
          set(initialState);
        },

        purgeOtherUsers: (studentId: string) => {
          if (!studentId) return;
          if (studentId !== progressStudentId) {
            progressGeneration += 1;
            progressStudentId = studentId;
          }
          set((state) => ({
            lessonProgress: filterByStudentId(state.lessonProgress, studentId),
            videoProgress: filterByStudentId(state.videoProgress, studentId),
            quizProgress: filterByStudentId(state.quizProgress, studentId),
            unitProgress: filterByStudentId(state.unitProgress, studentId),
            courseProgress: filterByStudentId(state.courseProgress, studentId),
            quizAnswers: filterByStudentId(state.quizAnswers, studentId),
            quizSessionState: filterByStudentId(state.quizSessionState, studentId),
            codingSubmissions: filterByStudentId(state.codingSubmissions, studentId),
            codingDrafts: filterByStudentId(state.codingDrafts, studentId),
            exerciseMetadata: filterByStudentId(state.exerciseMetadata, studentId),
            fetchedCodingSubmissionsForLessons: new Set(
              [...state.fetchedCodingSubmissionsForLessons].filter((key) =>
                key.startsWith(`${studentId}-`)
              )
            ),
          }));
        },
      }),
      {
        name: "progress-storage",
        // Server-owned progress must be fetched fresh. Persist only unfinished local work.
        partialize: selectPersistedProgressState,
        // Ignore server progress written by older versions of the app during rehydration.
        merge: mergePersistedProgressState,
      }
    )
  )
);

const filterByStudentId = <T extends Record<string, unknown>>(
  record: T,
  studentId: string
): T => {
  const prefix = `${studentId}-`;
  const entries = Object.entries(record).filter(([key]) =>
    key.startsWith(prefix)
  );
  return Object.fromEntries(entries) as T;
};

// Export selectors for easy access to specific progress data
export const useLessonProgress = (studentId: string, lessonId: number) => {
  return useProgressStore(
    (state) => state.lessonProgress[`${studentId}-${lessonId}`]
  );
};

export const useQuizProgress = (studentId: string, quizId: number) => {
  return useProgressStore(
    (state) => state.quizProgress[`${studentId}-${quizId}`]
  );
};

export const useQuizSessionState = (studentId: string, lessonId: number) => {
  return useProgressStore(
    (state) => state.quizSessionState[`${studentId}-${lessonId}`]
  );
};

export const useUnitProgress = (studentId: string, unitId: number) => {
  return useProgressStore(
    (state) => state.unitProgress[`${studentId}-${unitId}`]
  );
};

export const useCourseProgress = (studentId: string, courseId: number) => {
  return useProgressStore(
    (state) => state.courseProgress[`${studentId}-${courseId}`]
  );
};

export const useQuizAnswer = (studentId: string, questionId: number) => {
  return useProgressStore((state) => {
    const key = `${studentId}-${questionId}`;
    return state.quizAnswers[key];
  });
};

export const useCodingSubmission = (
  studentId: string,
  exerciseId: number
) => {
  return useProgressStore((state) => {
    const key = `${studentId}-${exerciseId}`;
    return state.codingSubmissions[key];
  });
};

export const useCodingDraft = (studentId: string, exerciseId: number) => {
  return useProgressStore((state) => {
    const key = `${studentId}-${exerciseId}`;
    return state.codingDrafts[key];
  });
};

export const useExerciseMetadata = (
  studentId: string,
  exerciseId: number
) => {
  return useProgressStore((state) => {
    const key = `${studentId}-${exerciseId}`;
    return state.exerciseMetadata[key];
  });
};
